import { AlertTriangle } from "lucide-react";
import { useCallback, useContext, useEffect, useState, type FC } from "react";

import { Alert, AlertDescription } from "@/components/podkit/alert/Alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { GeneralAccessSection } from "@/components/share/GeneralAccessSection";
import { GroupsWithAccessSection } from "@/components/share/GroupsWithAccessSection";
import { UsersWithAccessSection } from "@/components/share/UsersWithAccessSection";
import { ShareResourceContext, type ShareableResource } from "@/contexts/ShareResourceContext";
import { useResourceSharing } from "@/hooks/use-resource-sharing";
import { TrackLocations } from "@/hooks/use-segment";
import { useOrganization } from "@/queries/organization-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

const ShareResourceModalContent: FC<{ resource: ShareableResource }> = ({ resource }) => {
    const { isOpen, closeShareDialog } = useContext(ShareResourceContext);
    const { data: organization } = useOrganization();
    const [showProjectWarning, setShowProjectWarning] = useState(false);
    const [showRunnerWarning, setShowRunnerWarning] = useState(false);
    const [isSharedWithEveryone, setIsSharedWithEveryone] = useState(false);

    // Get initial sharing state
    const { isShared } = useResourceSharing(resource);

    // Check if organization is on Enterprise tier
    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;

    // Initialize isSharedWithEveryone when resource or isShared changes
    useEffect(() => {
        setIsSharedWithEveryone(isShared);
    }, [isShared]);

    const handleGroupsAdded = useCallback(
        (_groupIds: string[]) => {
            if (resource.resourceType === ResourceType.PROJECT) {
                setShowProjectWarning(true);
            }
        },
        [resource.resourceType],
    );

    const handleGroupRemoved = useCallback(
        (_groupId: string) => {
            if (resource.resourceType === ResourceType.RUNNER) {
                // Only show warning if not shared with everyone
                if (!isSharedWithEveryone) {
                    setShowRunnerWarning(true);
                }
            }
        },
        [resource.resourceType, isSharedWithEveryone],
    );

    const handleUsersAdded = useCallback(
        (_userIds: string[]) => {
            if (resource.resourceType === ResourceType.PROJECT) {
                setShowProjectWarning(true);
            }
        },
        [resource.resourceType],
    );

    const handleUserRemoved = useCallback(
        (_userId: string) => {
            if (resource.resourceType === ResourceType.RUNNER) {
                // Only show warning if not shared with everyone
                if (!isSharedWithEveryone) {
                    setShowRunnerWarning(true);
                }
            }
        },
        [resource.resourceType, isSharedWithEveryone],
    );

    const handleAccessLevelChange = useCallback(
        (isShared: boolean) => {
            setIsSharedWithEveryone(isShared);

            // Show project warning when changing to shared with everyone
            if (resource.resourceType === ResourceType.PROJECT && isShared) {
                setShowProjectWarning(true);
            }

            // Show runner warning when changing to not shared (restricted to groups)
            if (resource.resourceType === ResourceType.RUNNER && !isShared) {
                setShowRunnerWarning(true);
            }
        },
        [resource.resourceType],
    );

    const getTitle = () => {
        switch (resource.resourceType) {
            case ResourceType.PROJECT:
                return `Share ${resource.resourceName} project`;
            case ResourceType.RUNNER:
                return `Share ${resource.resourceName} runner`;
            case ResourceType.WORKFLOW:
                return `Share ${resource.resourceName} automation`;
            case ResourceType.GROUP:
                return `Share ${resource.resourceName} group`;
            default:
                return "Share";
        }
    };

    const isGroupResource = resource.resourceType === ResourceType.GROUP;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && closeShareDialog()}>
            <DialogContent
                className="max-w-xl"
                data-testid="share-resource-modal"
                data-track-location={TrackLocations.ShareResourceModal}
            >
                <DialogHeader>
                    <DialogTitle>{getTitle()}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4" data-testid={`${resource.resourceType}-sharing-content`}>
                    {isGroupResource ? (
                        <UsersWithAccessSection
                            resource={resource}
                            onUsersAdded={handleUsersAdded}
                            onUserRemoved={handleUserRemoved}
                        />
                    ) : (
                        <>
                            {isEnterpriseTier && (
                                <>
                                    <GroupsWithAccessSection
                                        resource={resource}
                                        onGroupsAdded={handleGroupsAdded}
                                        onGroupRemoved={handleGroupRemoved}
                                    />

                                    <div className="border-t border-border-light" />
                                    <UsersWithAccessSection
                                        resource={resource}
                                        onUsersAdded={handleUsersAdded}
                                        onUserRemoved={handleUserRemoved}
                                    />

                                    <div className="border-t border-border-light" />
                                </>
                            )}

                            <GeneralAccessSection resource={resource} onAccessLevelChange={handleAccessLevelChange} />

                            {showProjectWarning && (
                                <Alert variant="default">
                                    <AlertTriangle className="size-4 text-content-warning" />
                                    <AlertDescription>
                                        Users in these groups will not be able to use the project unless the underlying
                                        runner is also shared with them.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {showRunnerWarning && (
                                <Alert variant="default">
                                    <AlertTriangle className="size-4 text-content-warning" />
                                    <AlertDescription>
                                        Removing access to this runner may prevent users in these groups from using
                                        projects that depend on it.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {resource.resourceType === ResourceType.WORKFLOW && (
                                <Alert variant="default">
                                    <AlertTriangle className="size-4 text-content-warning" />
                                    <AlertDescription>
                                        Users with executor or admin access can run this automation. Executions will run
                                        with their permissions.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const ShareResourceModal: FC = () => {
    const { resource } = useContext(ShareResourceContext);

    if (!resource) return null;

    return <ShareResourceModalContent resource={resource} />;
};
